/* eslint-disable no-unused-vars */
import httpService from '@/services/httpService'
import _ from 'lodash'
import { convertUrlFiltersToURLParams } from '@/store/modules/rank-zone/Helper'
import Vue from 'vue'
import UrlPlanning from '../../../features/url-planning/UrlPlanning'
import CalendarDatePlanner from '../../../features/calendar/CalendarDatePlanner'

export const types = {
  ADD_URL_PLANNER_SESSION: 'rank-zone/ADD_URL_PLANNER_SESSION',
  ADD_URL_NO_DATE: 'rank-zone/ADD_URL_NO_DATE',
  ADD_URL_PLANNER_SETTINGS: 'rank-zone/ADD_URL_PLANNER_SETTINGS',
  ADD_URL_PLANNER_KEYWORDS: 'rank-zone/ADD_URL_PLANNER_KEYWORDS',
  ADD_URL_PLANNER_RESET: 'rank-zone/ADD_URL_PLANNER_KEYWORDS_RESET',
  ADD_URL_PLANNER_NO_DATE_RESET: 'rank-zone/ADD_URL_PLANNER_NO_DATE_RESET'
}
const emptyData = {
  urls: [],
  keywords: {},
  noDateUrls: [],
  settings: {
    sections: [],
    brief_statuses: [],
    url_statuses: [],
    url_attributes: []
  },
  metadata: {
    integrated_services: []
  }
}

const state = { ...emptyData }

const mutations = {
  [types.ADD_URL_PLANNER_SESSION]: function (state, { urls, metadata }) {
    state.urls = urls.map(url => Object.assign(url, { msv: Math.floor(url.msv) })) || []
    state.metadata = metadata
  },
  [types.ADD_URL_NO_DATE]: function (state, { urls }) {
    state.noDateUrls = urls || []
  },
  [types.ADD_URL_PLANNER_SETTINGS]: function (state, settings) {
    state.settings = { ...emptyData.settings, ...settings }
  },
  [types.ADD_URL_PLANNER_KEYWORDS]: function (state, { urlId, keywords }) {
    if (keywords) {
      // we need it to force Vue reactivity system to work properly
      Vue.set(state.keywords, urlId, keywords)
    } else {
      delete state.keywords[urlId]
    }
  },
  [types.ADD_URL_PLANNER_RESET]: function (state) {
    state = { ...emptyData }
  },
  [types.ADD_URL_PLANNER_NO_DATE_RESET]: function (state) {
    state.noDateUrls = []
  }
}

const actions = {
  getUrlPlannerData: async function ({ commit, dispatch }, { clientId, filters, clearData }) {
    const msv = JSON.parse(localStorage.getItem('msv')) || {}
    filters.msvField = msv[clientId] || 1
    // convert filters to url params
    const params = {
      msvField: msv[clientId] || 1
    }
    for (let filter in filters) {
      if (!filters.hasOwnProperty(filter)) {
        continue
      }
      const filterValue = filters[filter]
      if (!filterValue && filterValue !== 0) {
        continue
      }
      if (filterValue instanceof Array) {
        if (filterValue.length) {
          params[filter] = filterValue.join(',')
        }
      } else if (['msv', 'keywords'].includes(filter)) {
        if (filterValue.value) {
          params[filter] = filterValue.value
          params[`${filter}_operator`] = filterValue.operator
        }
      } else {
        params[filter] = filterValue
      }
    }
    clearData && dispatch('resetData')
    const response = await httpService.get(`/url/planning/${clientId}`, { params })
    if (!response || !response.data) {
      return
    }
    commit(types.ADD_URL_PLANNER_SESSION, { urls: response.data, metadata: response[ '_metadata' ] })
    return response.data
  },
  /**
   * Used in {@link CalendarDatePlanner}
   */
  getNoDateUrls: async function ({ commit, dispatch }, { clientId, filters, clearData }) {
    const params = { ...filters }
    for (let param in params) {
      if (params[param] instanceof Array) {
        params[param] = params[param].join(',')
      }
    }
    clearData && dispatch('resetNoDateData')
    const response = await httpService.get(`/url/no-date-urls/${clientId}`, { params })
    if (!response || !response.data) {
      return
    }
    commit(types.ADD_URL_NO_DATE, { urls: response.data })
    return response.data
  },
  /**
   * used for receiving settings for table filters
   */
  getUrlPlannerSettings: async function ({ commit, dispatch }, { clientId, filters }) {
    const { allowed_statuses } = filters
    const params = {}
    if (allowed_statuses instanceof Array) {
      params['allowed_statuses'] = allowed_statuses.join(',')
    }
    const response = await httpService.get(`/url/settings/${clientId}`, { params })
    if (!response || !response.data) {
      return
    }
    commit(types.ADD_URL_PLANNER_SETTINGS, response.data)
  },
  /**
   * Used to change url status
   */
  changeStatus: function ({ commit, dispatch }, { clientId, urlId, statusId = null, description = null, date = null, pageType = null }) {
    const params = { url_id: urlId, status_id: statusId, description, date, page_type_id: pageType }
    return httpService.patch(`/url/change-status/${clientId}`, params,
      { raiseError: true, successMessage: 'Status successfully updated!' })
  },
  /**
   * Used to change writing status
   */
  changeWritingStatus: function (storage, { successMessage = true, raiseError = true, ...params }) {
    const config = { raiseError }
    if (successMessage) {
      config.successMessage = 'Writing status successfully updated!'
    }
    return httpService.patch(`/url/change-writing-status`, params, config)
  },
  createUrl: function ({ commit }, { clientId, pageTypeId, sectionId, date, url }) {
    const params = {
      page_type_id: pageTypeId, section_id: sectionId, date, url
    }
    return httpService.post(`/url/create-url/${clientId}`, params,
      { successMessage: 'URL successfully created', raiseError: true, cancelErrorMessage: true })
  },
  /**
   * action for receiving keywords data for expanded url row
   * Used on screen {@link UrlPlanning}
   * @param filters - url planner filters.
   * @returns {Promise<void>}
   */
  getKeywordsByUrlIdData: async function ({ commit }, { clientId, filters }) {
    const msv = JSON.parse(localStorage.getItem('msv')) || {}
    filters.msv = msv[clientId] || 1
    let params = null
    if (filters && filters.filterParams) {
      params = convertUrlFiltersToURLParams(filters)
    } else {
      params = filters
    }
    const response = await httpService.get(`/reports/rankzone-report/keyword/${clientId}`, { params }, { encode: false })
    if (!response || !response.data) {
      return
    }
    const keywords = _.get(response, 'data.keywords', [])
    commit(types.ADD_URL_PLANNER_KEYWORDS, { keywords, urlId: filters['url_id'] })
  },
  /**
   * clear already received by component url's keywords. is used to avoid clogging memory with extra data
   * @param urlId
   */
  removeKeywordsByIrlId ({ commit }, { urlId }) {
    commit(types.ADD_URL_PLANNER_KEYWORDS, { urlId })
  },
  resetData: function ({ commit }) {
    commit(types.ADD_URL_PLANNER_RESET)
  },
  resetNoDateData: function ({ commit }) {
    commit(types.ADD_URL_PLANNER_RESET)
  }
}

/**
 * Store for manage {@link UrlPlanning}
 */
export default {
  namespaced: true,
  state,
  actions,
  mutations
}
